/**
 * A small fine-grained reactive core.
 *
 * Sources (signals and memos) carry a version. Computations record the version of every source
 * they read; when a source changes, dependents are marked stale and effects are queued. Before an
 * effect re-runs it pulls its memo dependencies, so a memo that recomputes to an equal value does
 * not wake anything downstream. Computations own the computations and cleanups created while they
 * run, and dispose them before re-running.
 */

export type Accessor<T> = () => T
export type Dispose = () => void

export interface Signal<T> extends Accessor<T> {
  set(value: T): void
  update(fn: (value: T) => T): void
  peek(): T
}

interface Source {
  version: number
  observers: Set<Computation>
  /** Memos refresh lazily before their version is compared. */
  refresh?(): void
}

interface Owner {
  children: Computation[] | null
  cleanups: (() => void)[] | null
  owner: Owner | null
  disposed: boolean
}

interface Computation extends Owner {
  readonly id: number
  readonly effect: boolean
  fn: () => void
  deps: Map<Source, number>
  stale: boolean
}

let tracking: Computation | null = null
let currentOwner: Owner | null = null
let batchDepth = 0
let nextId = 1
const queue = new Map<number, Computation>()
let flushing = false

function track(source: Source): void {
  if (tracking && !tracking.disposed) {
    tracking.deps.set(source, source.version)
    source.observers.add(tracking)
  }
}

function markStale(source: Source): void {
  for (const observer of [...source.observers]) {
    if (observer.stale || observer.disposed) continue
    observer.stale = true
    if (observer.effect) queue.set(observer.id, observer)
    else {
      const memo = memoSources.get(observer)
      if (memo) markStale(memo)
    }
  }
}

const memoSources = new WeakMap<Computation, Source>()

function flush(): void {
  if (flushing || batchDepth > 0) return
  flushing = true
  try {
    let guard = 0
    while (queue.size > 0) {
      if (++guard > 100_000) throw new Error('Reactive update loop did not settle')
      const [id, computation] = [...queue.entries()].sort((a, b) => a[0] - b[0])[0]!
      queue.delete(id)
      if (computation.disposed || !computation.stale) continue
      if (!changed(computation)) {
        computation.stale = false
        continue
      }
      run(computation)
    }
  } finally {
    flushing = false
  }
}

function changed(computation: Computation): boolean {
  if (computation.deps.size === 0) return true
  for (const [source, version] of computation.deps) {
    source.refresh?.()
    if (source.version !== version) return true
  }
  return false
}

function cleanOwner(owner: Owner): void {
  if (owner.children) {
    for (const child of owner.children) disposeComputation(child)
    owner.children = null
  }
  if (owner.cleanups) {
    const cleanups = owner.cleanups
    owner.cleanups = null
    for (let i = cleanups.length - 1; i >= 0; i--) cleanups[i]!()
  }
}

function unlink(computation: Computation): void {
  for (const source of computation.deps.keys()) source.observers.delete(computation)
  computation.deps.clear()
}

function disposeComputation(computation: Computation): void {
  if (computation.disposed) return
  computation.disposed = true
  queue.delete(computation.id)
  unlink(computation)
  cleanOwner(computation)
}

function run(computation: Computation): void {
  cleanOwner(computation)
  unlink(computation)
  const previousTracking = tracking
  const previousOwner = currentOwner
  tracking = computation
  currentOwner = computation
  computation.stale = false
  try {
    computation.fn()
  } finally {
    tracking = previousTracking
    currentOwner = previousOwner
  }
}

function createComputation(fn: () => void, effect: boolean): Computation {
  const computation: Computation = {
    id: nextId++, effect, fn, deps: new Map(), stale: false,
    children: null, cleanups: null, owner: currentOwner, disposed: false,
  }
  if (currentOwner) (currentOwner.children ??= []).push(computation)
  return computation
}

export function signal<T>(initial: T, equals: (a: T, b: T) => boolean = Object.is): Signal<T> {
  let value = initial
  const source: Source = { version: 0, observers: new Set() }
  const read = (() => {
    track(source)
    return value
  }) as Signal<T>
  read.peek = () => value
  read.set = (next: T) => {
    if (equals(value, next)) return
    value = next
    source.version++
    markStale(source)
    flush()
  }
  read.update = (fn) => read.set(fn(value))
  return read
}

/** A cached derivation. Recomputes lazily when read after a dependency changed. */
export function computed<T>(fn: () => T, equals: (a: T, b: T) => boolean = Object.is): Accessor<T> {
  let value: T
  let initialized = false
  const source: Source = {
    version: 0,
    observers: new Set(),
    refresh() {
      if (initialized && !computation.stale) return
      if (initialized && !changed(computation)) {
        computation.stale = false
        return
      }
      const previous = value
      run(computation)
      if (!initialized || !equals(previous, value)) source.version++
      initialized = true
    },
  }
  const computation = createComputation(() => { value = fn() }, false)
  memoSources.set(computation, source)
  return () => {
    source.refresh!()
    track(source)
    return value
  }
}

/** Runs `fn` now and again whenever anything it read changes. Returns a disposer. */
export function effect(fn: () => void | (() => void)): Dispose {
  const computation = createComputation(() => {
    const cleanup = fn()
    if (typeof cleanup === 'function') onCleanup(cleanup)
  }, true)
  run(computation)
  return () => disposeComputation(computation)
}

/** Registers a cleanup with the current owner (computation or root). */
export function onCleanup(fn: () => void): void {
  if (currentOwner) (currentOwner.cleanups ??= []).push(fn)
}

/** Groups writes so effects run once after `fn` returns. */
export function batch<T>(fn: () => T): T {
  batchDepth++
  try {
    return fn()
  } finally {
    batchDepth--
    flush()
  }
}

/** Reads without tracking. */
export function untrack<T>(fn: () => T): T {
  const previous = tracking
  tracking = null
  try {
    return fn()
  } finally {
    tracking = previous
  }
}

/** An ownership scope that is not itself reactive. `dispose` tears down everything created inside. */
export function root<T>(fn: (dispose: Dispose) => T): T {
  const owner: Owner = { children: null, cleanups: null, owner: currentOwner, disposed: false }
  const previousOwner = currentOwner
  const previousTracking = tracking
  currentOwner = owner
  tracking = null
  const dispose = () => {
    if (owner.disposed) return
    owner.disposed = true
    cleanOwner(owner)
  }
  try {
    return fn(dispose)
  } finally {
    currentOwner = previousOwner
    tracking = previousTracking
  }
}

/** Captures the current owner so callbacks (events, timers) can create owned computations later. */
export function getOwner(): unknown {
  return currentOwner
}

export function runWithOwner<T>(owner: unknown, fn: () => T): T {
  const previous = currentOwner
  currentOwner = owner as Owner | null
  try {
    return fn()
  } finally {
    currentOwner = previous
  }
}
